import _ from "lodash";
import React, { useState, useEffect, memo, useMemo } from "react";
import classes from "./EventBody.scss";
import AnimateHeight from "UIComponents/AnimateHeight";
import TimeSelectorV2 from "UIComponents/TimeSelectorV2";
import { BottomArrow } from "SvgComponents";
import classNames from "classnames";
import {
  getStringAutoLinks,
  isUserTypeStaff,
  replaceSlashNWithBRTag,
  onEnterOrSpaceKeyDown,
  getStyleStrippedText,
} from "Utils";
import { colors } from "Constants";
import moment from "moment";
import {
  START_PICKER_START_TIME,
  START_PICKER_END_TIME,
  getDateLabel,
  getVideoMeetingDateLabel,
  getSubmissionOpenLabel,
  getSubmissionCloseLabel,
  isNewParticipantSelectorEnabled,
} from "MyCalendar/modules/Utils";
import { MY_CALENDAR_CONSTANTS } from "MyCalendar/constants/stringConstants";
import { CopyOutlined } from "@toddle-edu/ds-icons";
import {
  DatePicker,
  Tag,
  IconButton,
  Tooltip,
  ProgressIndicator,
} from "@toddle-edu/ds-web";
import UserNameAvatar from "AppComponents/UserNameAvatar";
import { DATE_TIME_CONSTANTS } from "Constants/dateTimeConstants";
import { copyToClipboard } from "Utils/CopyToClipboard";
import {
  formatDate,
  getDateMomentObjectCustom,
  isFutureDateTime,
  getDateMomentObject,
  getISOString,
  isValidDateTimeString,
  getIncrementedDate,
  checkIsDateAfter,
} from "Utils/DateTimeHelpers";
import ConditionalWrapper from "Utils/ConditionalWrapper";
import {
  getSubmissionStatusIconWithTooltip,
  getSubmissionStatus,
  getTimeRangeProps,
  getIsSubmissionOpen,
  isNewQuickViewEditFFEnabled,
} from "../../modules/utils";
import { isNewVideoMeetingUIFeatureEnabled } from "MyCalendar/components/EventModalV2/modules/Utils";
import {
  getOpenDateRange,
  getDueDateRange,
  getCloseDateRange,
  getMomentFromDateAndTimeString,
} from "ClassRoom/components/AddAssignment/AssignmentSettings/Utils";
import { getScheduleOrDeadlineTime } from "ClassRoom/modules/utils";
import { getSettingValue } from "modules/PermissionModule";
import AssignmentDateTimeFields from "../AssignmentDateTimeFields/AssignmentDateTimeFields";
import { useI18n } from "Hooks";
import { useAssignmentDateValidation } from "../../hooks";
import EventBodyMeeting from "./EventBodyMeeting";
import ParticipantsSummary from "../../../ParticipantsSummary";
import SkeletonLoader from "UIComponents/SkeletonLoader";

const styles = {
  timeSelectorDropdownContainerStyle: {
    top: "-11px",
    left: "-1px",
    width: "128px",
    position: "absolute",
  },
  timeSelectorDropdownItemStyle: {
    display: "flex",
    width: "100%",
  },
  timeSelectorInputStyle: {
    border: "none",
    height: "24px",
    padding: "4px 8px",
    fontWeight: "500",
    fontSize: "1.4rem",
    color: colors.textDefault,
    textAlign: "center",
    pointerEvents: "none",
  },
  timeSelectorTimeComponentStyle: {
    padding: "0",
    height: "unset",
    width: "84px",
    backgroundColor: "var(--surface-primary-active)",
    zIndex: "0",
    borderRadius: "4px",
  },
  timeSelectorDropdownParentContainerStyle: {
    width: "100%",
  },
  avatarStyles: {
    size: "small",
    randomizeColor: true,
    shade: "light",
    shape: "square",
  },
  timeSelectorContainerStyle: {
    width: "unset",
    marginLeft: "8px",
  },
  tooltipContainer: {
    width: "240px",
  },
};

const ParticipantLoadingSkeleton = () => {
  return (
    <>
      <SkeletonLoader
        width="40%"
        height="20px"
        customClasses={classes.loaderStyle}
      />
      <SkeletonLoader
        width="100%"
        height="20px"
        customClasses={classes.loaderStyle}
      />
    </>
  );
};

const CalendarLabel = ({
  title,
  userCount,
  hasMoreLabel = false,
  isCalendarAssignmentAssignFlowRevampEnabled,
  isStaff,
}) => {
  const { t } = useI18n();
  return (
    <div className={classes.calendarLabel}>
      <div className={classNames("text-body", classes.title)}>
        {title}
        {isNewQuickViewEditFFEnabled() && isStaff ? (
          <span className={classNames("text-body", classes.sharedWithLabel)}>
            ({t("calendar:shared_with_count_student", { count: userCount })})
          </span>
        ) : null}
      </div>
      {!isCalendarAssignmentAssignFlowRevampEnabled && !_.isNull(userCount) ? (
        <div className={classNames("text-body", classes.userCount)}>
          &nbsp; | {userCount} students{hasMoreLabel ? "," : ""} &nbsp;
        </div>
      ) : null}
    </div>
  );
};

const CalendarUnifiedLabel = ({
  t,
  calendarWiseAssignedUsers,
  isStaff,
  isVideoMeeting,
  isCalendarAssignmentAssignFlowRevampEnabled,
}) => {
  const totalAssignedUsers = _.reduce(
    calendarWiseAssignedUsers,
    (result, { assignedUsers }) => {
      return result + _.size(assignedUsers);
    },
    0
  );

  const firstClass = _.first(calendarWiseAssignedUsers);

  const totalClasses = _.size(calendarWiseAssignedUsers);

  return totalClasses > 1 && (isStaff || isVideoMeeting) ? (
    <CalendarLabel
      title={`${totalClasses} ${t("common:class_lowercase", {
        count: totalClasses,
      })}`}
      userCount={isStaff ? totalAssignedUsers : null}
      isCalendarAssignmentAssignFlowRevampEnabled={
        isCalendarAssignmentAssignFlowRevampEnabled
      }
      isStaff={isStaff}
    />
  ) : (
    <CalendarLabel
      title={_.get(firstClass, "title", "")}
      userCount={isStaff ? _.get(firstClass, "totalUserCount", "") : null}
      isCalendarAssignmentAssignFlowRevampEnabled={
        isCalendarAssignmentAssignFlowRevampEnabled
      }
      isStaff={isStaff}
    />
  );
};

const NewQuickViewFeatureFlagWrapper = ({ children, Component = null }) => {
  return isNewQuickViewEditFFEnabled() ? Component : children;
};

const CalendarWiseAssignedUsers = React.memo(props => {
  const {
    t,
    calendarWiseAssignedUsers,
    userId,
    isParent,
    childId,
    calendars,
    testId,
    isStaff,
    submissionsSummaryCounts,
    isVideoMeeting,
    isDueDatePassed,
    isCalendarAssignmentAssignFlowRevampEnabled,
    isStudentSubmissionEnabled,
  } = props;
  const [isCourseStudentsVisible, setIsCourseStudentsVisible] = useState(false);

  const arrowIconStyle = classNames(
    { [classes.arrowIcon]: true },
    { [classes.arrowIconReversed]: isCourseStudentsVisible }
  );

  const toggle = () => {
    if (isStaff || isVideoMeeting) setIsCourseStudentsVisible(state => !state);
  };

  const handleKeyDown = event => {
    onEnterOrSpaceKeyDown(event, toggle);
  };

  // if user is student or parent, make sure they only classes they are part of
  const studentMappedCourses = _.reduce(
    calendars,
    (acc, { parent }) => {
      const isStudentPartOfThisCourse = _.find(
        parent?.allStudents?.edges,
        ({ node }) => _.isEqual(node?.id, isParent ? childId : userId)
      );
      if (isStudentPartOfThisCourse) {
        acc[parent?.id] = true;
      }
      return acc;
    },
    {}
  );

  const calendarWiseAssignedUserClassList = useMemo(() => {
    return _.filter(
      calendarWiseAssignedUsers,
      calendar => studentMappedCourses[calendar?.id] || isStaff // for staff show all classes
    );
  }, [calendarWiseAssignedUsers]);

  const totalSubmissions = _.get(
    submissionsSummaryCounts,
    "totalSubmissions",
    0
  );
  const totalAssignedStudents = _.get(
    submissionsSummaryCounts,
    "totalAssignedStudents",
    0
  );

  const submissionPercentage =
    totalAssignedStudents > 0
      ? (totalSubmissions / totalAssignedStudents) * 100
      : 0;

  const assignedToContainer = classNames("text-label", {
    [classes.assignedToLabel]: true,
    [classes.assignedToLabelParentOrStudent]: !isStaff,
  });

  const assignedCalUsersContainer = classNames({
    [classes.assignedCalUsersContainer]: true,
    [classes.assignedCalUsersContainerParentOrStudent]: !isStaff,
  });

  const fractionStudentStatusContainer = classNames(
    "text-body",
    classes.fractionStudentStatusContainer,
    { [classes.notSubmittedSummaryText]: isDueDatePassed }
  );

  const progressIndicatorContainer = classNames({
    [classes.progressPreviewLoader]: true,
    [classes.progressPreviewLoaderDue]: isDueDatePassed,
  });

  const courseDetailsHeaderContainer = classNames({
    [classes.courseDetailsHeader]: true,
    [classes.courseDetailsHeaderUninteractive]: !isStaff,
  });

  const isNewQuickViewEnabled = isNewQuickViewEditFFEnabled();

  const courseDetailsClassContainer = classNames({
    [classes.courseDetails]: true,
    [classes.courseDetailsV2]: isNewQuickViewEnabled,
  });
  return (
    <div className={courseDetailsClassContainer}>
      <div
        className={courseDetailsHeaderContainer}
        tabIndex={isStaff ? 0 : 1}
        onKeyDown={handleKeyDown}
        onClick={toggle}
      >
        <div className={classes.left}>
          <div className={assignedToContainer}>
            {isVideoMeeting ? t("common:assigned_to") : t("common:class")}
          </div>
          {!isCourseStudentsVisible || !isVideoMeeting ? (
            <div className={classes.courseSummary}>
              <CalendarUnifiedLabel
                t={t}
                calendarWiseAssignedUsers={calendarWiseAssignedUserClassList}
                isStaff={isStaff}
                isVideoMeeting={isVideoMeeting}
                isCalendarAssignmentAssignFlowRevampEnabled={
                  isCalendarAssignmentAssignFlowRevampEnabled
                }
              />
              <NewQuickViewFeatureFlagWrapper>
                {isStaff &&
                !isVideoMeeting &&
                isCalendarAssignmentAssignFlowRevampEnabled &&
                isStudentSubmissionEnabled ? (
                  <div className={fractionStudentStatusContainer}>
                    <div className={progressIndicatorContainer}>
                      <ProgressIndicator
                        variant="preview-loader"
                        progress={submissionPercentage}
                      ></ProgressIndicator>
                    </div>
                    {isDueDatePassed
                      ? t("calendar:count_submissions_pending", {
                          count: totalAssignedStudents - totalSubmissions,
                        })
                      : t("calendar:fraction_students_submitted_assignment", {
                          fraction: `${totalSubmissions}/${totalAssignedStudents}`,
                        })}
                  </div>
                ) : null}
              </NewQuickViewFeatureFlagWrapper>
            </div>
          ) : null}
        </div>
        <div className={classes.right} data-test-id={`${testId}-assigned-to`}>
          {isStaff || isVideoMeeting ? (
            <div className={arrowIconStyle}>
              <BottomArrow fill={colors.neutral300} width={20} height={20} />
            </div>
          ) : null}
        </div>
      </div>
      <div>
        <AnimateHeight
          height={isCourseStudentsVisible ? "auto" : 0}
          duration={300}
          onAnimationStart={null}
          onAnimationEnd={null}
          animateOpacity={true}
        >
          <div className={classes.courseDetailsBody}>
            {_.map(calendarWiseAssignedUserClassList, calendar => {
              const { id, title, assignedUsers } = calendar;
              return (
                <div
                  key={`class-calendar-${id}`}
                  className={assignedCalUsersContainer}
                >
                  {isVideoMeeting ? (
                    <div className={classes.assignedCalendarLabel}>
                      <CalendarLabel
                        title={title}
                        userCount={_.size(assignedUsers)}
                        isCalendarAssignmentAssignFlowRevampEnabled={
                          isCalendarAssignmentAssignFlowRevampEnabled
                        }
                        isStaff={isStaff}
                      />
                    </div>
                  ) : null}
                  {isStaff ? (
                    <div className={classes.studentListContainer}>
                      {_.map(
                        assignedUsers,
                        (
                          { id, name, profileImage, statusV2, alertTypes },
                          currentIndex
                        ) => {
                          const studentAlertsConfig = {
                            studentId: id,
                            alertTypes,
                          };

                          const isLastUser =
                            currentIndex === _.size(assignedUsers) - 1;
                          const isFirstUser = currentIndex === 0;
                          return (
                            <>
                              <div
                                key={`student-${id}}`}
                                className={classes.studentRow}
                              >
                                <div
                                  className={classNames(
                                    classes.studentProfile,
                                    isNewQuickViewEnabled && !isFirstUser
                                      ? classes.studentProfileWithMarginTop
                                      : null
                                  )}
                                >
                                  <UserNameAvatar
                                    id={id}
                                    profileImage={profileImage}
                                    name={name}
                                    shouldDynamicColor={true}
                                    showAvatar={true}
                                    avatarStyles={{
                                      ...styles.avatarStyles,
                                      shape: isNewQuickViewEnabled
                                        ? "circle"
                                        : "square",
                                    }}
                                    studentAlertsConfig={studentAlertsConfig}
                                    profileCascadeDsVersion="2.0"
                                    dsVersion="2.0"
                                    customNameClass={classes.userNameLabel}
                                  />
                                </div>
                                <NewQuickViewFeatureFlagWrapper>
                                  {isCalendarAssignmentAssignFlowRevampEnabled
                                    ? getSubmissionStatusIconWithTooltip({
                                        t,
                                        statusV2,
                                        isDueDatePassed,
                                      })
                                    : null}
                                </NewQuickViewFeatureFlagWrapper>
                              </div>
                              {isNewQuickViewEnabled && !isLastUser ? (
                                <div className={classes.horizontalDivider} />
                              ) : null}
                            </>
                          );
                        }
                      )}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </AnimateHeight>
      </div>
    </div>
  );
});

CalendarWiseAssignedUsers.displayName = "CalendarWiseAssignedUsers";

const TimeSelectorByPerm = props => {
  const {
    value,
    updateInputField,
    startTime,
    endTime,
    disableConfirmButton,
    scheduleDate,
    isCurrentAcademicYear,
    isGradingPeriodUnlocked = true,
    lockedTermDisabledTooltip,
    testId,
    canManageAssignment = true,
    showError = false,
  } = props;

  const [timeHover, setTimeHover] = useState(false);

  const timeComponentStyle = {
    ...styles.timeSelectorTimeComponentStyle,

    ...(showError
      ? { backgroundColor: "var(--tag-background-pink-default)" }
      : {}),

    ...(timeHover
      ? {
          backgroundColor: showError
            ? "var(--tag-background-pink-hover)"
            : "var(--surface-secondary-active)",
        }
      : {}),
  };

  const finalTimeSelectorInputStyle = {
    ...styles.timeSelectorInputStyle,
    ...(showError ? { color: "var(--tag-foreground-pink)" } : {}),
  };

  const onMouseEnterHandler = () => setTimeHover(true);
  const onMouseLeaveHandler = () => setTimeHover(false);

  return (
    <div onMouseEnter={onMouseEnterHandler} onMouseLeave={onMouseLeaveHandler}>
      <ConditionalWrapper
        condition={!isGradingPeriodUnlocked}
        wrapper={<Tooltip tooltip={lockedTermDisabledTooltip} />}
        type={"wrapperAsElement"}
      >
        <div>
          <TimeSelectorV2
            showInputIcon={false}
            value={value}
            SelectedTimeformat="HH:mm"
            isValueMomentObj={false}
            updateInputField={updateInputField}
            isDisabled={
              _.isEmpty(scheduleDate) ||
              !isCurrentAcademicYear ||
              !isGradingPeriodUnlocked ||
              !canManageAssignment
            }
            startTime={startTime}
            endTime={endTime}
            dropdownItemStyle={styles.timeSelectorDropdownItemStyle}
            dropdownContainerStyle={styles.timeSelectorDropdownContainerStyle}
            containerStyle={styles.timeSelectorContainerStyle}
            inputStyle={finalTimeSelectorInputStyle}
            timeComponentStyle={timeComponentStyle}
            showBorder={false}
            disableConfirmButton={disableConfirmButton}
            alwaysShowTooltip={true}
            showInputBorderOnFocus={true}
            positionDropdownDivWithPopover={true}
            dropdownParentContainerStyle={
              styles.timeSelectorDropdownParentContainerStyle
            }
            dropdownParentStyle={
              styles.timeSelectorDropdownParentContainerStyle
            }
            showPlaceholderWhenDisabled={false}
            minuteStep={5}
            popoverZIndex={1001}
            testId={`${testId}-UIComponents-timeSelectorV2`}
          ></TimeSelectorV2>
        </div>
      </ConditionalWrapper>
    </div>
  );
};

const EventBody = props => {
  const {
    title,
    description,
    meetingLink,
    meetingType,
    meetingCode,
    meetingPasscode,
    publishedTime,
    submissionDeadlineTime,
    calendarWiseAssignedUsers,
    onRescheduleEventClick,
    rescheduleDataChangesLocal,
    rescheduleData,
    setFooterDisabled,
    userId,
    userType,
    itemType,
    t,
    isNotUserCourse,
    isUserCourse,
    showCalendarAssignmentByDueDate,
    isCurrentAcademicYear,
    isViewOnlyTag,
    state,
    isVideoMeeting,
    isParent,
    childId,
    calendars,
    academicYearStartDate,
    academicYearEndDate,
    testId,
    deadline,
    isGradingPeriodUnlocked = true,
    allowAssignmentScheduleEndTimeChange,
    canManageAssignment = true,
    submissionsSummaryCounts,
    visibility,
    isCalendarAssignmentAssignFlowRevampEnabled,
    currentCurriculumProgramId,
    assignmentState,
    isWorksheetWithFixedStartTime,
    isStudentProfileCalendarView,
    isStudentSubmissionEnabled,
    isPrivate,
    summaryCounts,
    errorMsgLocalesRef,
    associatedEntities,
    participantListType,
    isAssociatedEntitiesDetailsLoading,
    users,
    sharedWith,
    attachments,
    repeatEventSettings,
    recurrenceStart,
    startTime,
    metadata,
    canUserEditThisMeeting,

    // loading states
    isMeetingDetailsLoading,
  } = props;

  const isDraft = state === "DRAFT";

  const {
    // Following used for meetings
    scheduleDate,
    scheduleStartTime,
    scheduleEndTime,

    // Following used for Assignments
    publishedAt, //zulu time ISO string
    calendarAssignmentEndAt, //zulu time ISO string
    dueDate, //zulu time ISO string
    closeSubmissionDate, //zulu time ISO string
  } = rescheduleData;
  const onRescheduleEventClickLocal = () => {
    onRescheduleEventClick(true);
  };

  const [isFromTimeInvalid, setIsFromTimeInvalid] = useState(false);
  const [isToTimeInvalid, setIsToTimeInvalid] = useState(false);

  useEffect(() => {
    if (isFromTimeInvalid || isToTimeInvalid) {
      setFooterDisabled(true);
    } else {
      setFooterDisabled(false);
    }
  }, [isFromTimeInvalid, isToTimeInvalid]);

  const onStartTimeChange = (param, endTime) => {
    onRescheduleEventClickLocal();
    rescheduleDataChangesLocal({
      scheduleStartTime: param,
      scheduleEndTime:
        endTime ?? moment(param, "HH:mm").add(30, "minutes").format("HH:mm"),
    });
  };

  const onEndTimeChange = param => {
    onRescheduleEventClickLocal();
    rescheduleDataChangesLocal({
      scheduleEndTime: param,
    });
  };

  const onUpdateDateInput = selectedDate => {
    onRescheduleEventClickLocal();
    const newDate = formatDate({
      date: selectedDate,
      format: DATE_TIME_CONSTANTS.YYYY_MM_DD,
    });
    rescheduleDataChangesLocal({ scheduleDate: newDate });
  };

  const onUpdateDueDateState = selectedDate => {
    onRescheduleEventClickLocal();
    rescheduleDataChangesLocal({
      dueDate: getISOString(selectedDate),
    });
  };

  const onUpdateDueDate = newDate => {
    const newDateTime = getISOString(
      getDateMomentObjectCustom(
        `${formatDate({
          date: newDate,
          format: DATE_TIME_CONSTANTS.YYYY_MM_DD,
        })}:${formatDate({
          date: dueDate,
          format: DATE_TIME_CONSTANTS.HH_mm,
        })}`,
        DATE_TIME_CONSTANTS.YYYY_MM_DD__HH_mm
      )
    );
    onUpdateDueDateState(newDateTime);
  };

  const onUpdateDueTime = newTime => {
    const newDateTime = getISOString(
      getDateMomentObjectCustom(
        `${formatDate({
          date: dueDate,
          format: DATE_TIME_CONSTANTS.YYYY_MM_DD,
        })}:${newTime}`,
        DATE_TIME_CONSTANTS.YYYY_MM_DD__HH_mm
      )
    );
    onUpdateDueDateState(newDateTime);
  };

  const onUpdatePublishedAtState = selectedDate => {
    onRescheduleEventClickLocal();
    rescheduleDataChangesLocal({
      publishedAt: getISOString(selectedDate),
      calendarAssignmentEndAt: getIncrementedDate({
        date: selectedDate,
        key: DATE_TIME_CONSTANTS.minutes,
        incrementBy: 30,
      }),
    });
  };

  const onUpdateCalendarAssignmentEndAtState = selectedDate => {
    onRescheduleEventClickLocal();
    rescheduleDataChangesLocal({
      calendarAssignmentEndAt: getISOString(selectedDate),
    });
  };

  const onUpdatePublishedDate = newDate => {
    const newDateTime = getISOString(
      getDateMomentObjectCustom(
        `${formatDate({
          date: newDate,
          format: DATE_TIME_CONSTANTS.YYYY_MM_DD,
        })}:${formatDate({
          date: publishedAt,
          format: DATE_TIME_CONSTANTS.HH_mm,
        })}`,
        DATE_TIME_CONSTANTS.YYYY_MM_DD__HH_mm
      )
    );
    onUpdatePublishedAtState(newDateTime);
  };

  const onUpdatePublishedTime = newTime => {
    const newDateTime = getISOString(
      getDateMomentObjectCustom(
        `${formatDate({
          date: publishedAt,
          format: DATE_TIME_CONSTANTS.YYYY_MM_DD,
        })}:${newTime}`,
        DATE_TIME_CONSTANTS.YYYY_MM_DD__HH_mm
      )
    );
    onUpdatePublishedAtState(newDateTime);
  };

  const onUpdateCalendarAssignmentEndAt = newTime => {
    const newDateTime = getISOString(
      getDateMomentObjectCustom(
        `${formatDate({
          date: calendarAssignmentEndAt,
          format: DATE_TIME_CONSTANTS.YYYY_MM_DD,
        })}:${newTime}`,
        DATE_TIME_CONSTANTS.YYYY_MM_DD__HH_mm
      )
    );
    onUpdateCalendarAssignmentEndAtState(newDateTime);
  };

  const onUpdateCloseSubmissionDateState = selectedDate => {
    onRescheduleEventClickLocal();
    rescheduleDataChangesLocal({
      closeSubmissionDate: getISOString(selectedDate),
    });
  };

  const onUpdateCloseSubmissionDate = newDate => {
    const newDateTime = getISOString(
      getDateMomentObjectCustom(
        `${formatDate({
          date: newDate,
          format: DATE_TIME_CONSTANTS.YYYY_MM_DD,
        })}:${formatDate({
          date: closeSubmissionDate,
          format: DATE_TIME_CONSTANTS.HH_mm,
        })}`,
        DATE_TIME_CONSTANTS.YYYY_MM_DD__HH_mm
      )
    );
    onUpdateCloseSubmissionDateState(newDateTime);
  };

  const onUpdateCloseSubmissionTime = newTime => {
    const newDateTime = getISOString(
      getDateMomentObjectCustom(
        `${formatDate({
          date: closeSubmissionDate,
          format: DATE_TIME_CONSTANTS.YYYY_MM_DD,
        })}:${newTime}`,
        DATE_TIME_CONSTANTS.YYYY_MM_DD__HH_mm
      )
    );
    onUpdateCloseSubmissionDateState(newDateTime);
  };

  const isVaildDueDate = isValidDateTimeString(dueDate);
  const dueDateMomentObject = getDateMomentObject(dueDate);
  const isDueDatePassed = checkIsDateAfter({
    date1: getDateMomentObject(),
    date2: dueDateMomentObject,
    key: "minute",
  });
  const publishedAtMomentObject = getDateMomentObject(publishedAt);
  const calendarAssignmentEndAtMomentObject = getDateMomentObject(
    calendarAssignmentEndAt
  );
  const isVaildCloseSubmissionDate = isValidDateTimeString(closeSubmissionDate);
  const closeSubmissionDateMomentObject = getDateMomentObject(
    closeSubmissionDate
  );
  const isCloseSubmissionDatePassed = checkIsDateAfter({
    date1: getDateMomentObject(),
    date2: closeSubmissionDateMomentObject,
    key: "minute",
  });

  const isStaff = isUserTypeStaff({ userType });
  const isAssignment = itemType === MY_CALENDAR_CONSTANTS.ASSIGNMENT;

  const maxStartDate = getDateMomentObject(academicYearEndDate);

  const onCopyURL = () => {
    copyToClipboard(meetingLink);
  };

  const lockedTermDisabledTooltip = (
    <div style={styles.tooltipContainer}>
      {t("academicSetup:you_cannot_make_changes_to_assignment_since_locked")}
    </div>
  );

  const editableDueDateTimeStyles = classNames({
    [classes.dateTimeContainer]: true,
    [classes.datePickerError]:
      isCalendarAssignmentAssignFlowRevampEnabled && isDueDatePassed,
  });

  const editableCloseSubmissionDateStyles = classNames({
    [classes.dateTimeContainer]: true,
    [classes.datePickerError]:
      isCalendarAssignmentAssignFlowRevampEnabled &&
      isCloseSubmissionDatePassed,
  });

  const nonEditableDueDateTimeStyles = classNames("text-body", {
    [classes.published]: true,
    [classes.nonEditableTimeRed]:
      isCalendarAssignmentAssignFlowRevampEnabled && isDueDatePassed,
  });

  const nonEditableCloseSubmissionDateStyles = classNames("text-body", {
    [classes.published]: true,
    [classes.nonEditableTimeRed]:
      isCalendarAssignmentAssignFlowRevampEnabled &&
      isCloseSubmissionDatePassed,
  });

  // This will be the time in 24hr format of next slot from current moment according to 15 mins interval
  // eg. current time 13:12, this will give 13:15
  const { time: initialTime } = getScheduleOrDeadlineTime({
    date: getDateMomentObject(),
  });
  const initialTimeMoment = getMomentFromDateAndTimeString({
    timeString: initialTime,
  });
  const dueOn = getMomentFromDateAndTimeString({
    dateString: formatDate({
      date: dueDateMomentObject,
      format: DATE_TIME_CONSTANTS.YYYY_MM_DD,
    }),
    timeString: formatDate({
      date: dueDateMomentObject,
      format: DATE_TIME_CONSTANTS.HH_mm,
    }),
  });
  const opensOn = getMomentFromDateAndTimeString({
    dateString: formatDate({
      date: publishedAtMomentObject,
      format: DATE_TIME_CONSTANTS.YYYY_MM_DD,
    }),
    timeString: formatDate({
      date: publishedAtMomentObject,
      format: DATE_TIME_CONSTANTS.HH_mm,
    }),
  });
  const closesOn = getMomentFromDateAndTimeString({
    dateTimeString: formatDate({
      date: closeSubmissionDateMomentObject,
      format: DATE_TIME_CONSTANTS.YYYY_MM_DD__HH_mm,
    }),
  });

  const allowBackDate = getSettingValue({
    name: "Common:BackDateAssignment",
    currentCurriculumProgramId,
  });

  const openDateRange = getOpenDateRange({
    academicYearStartDate,
    academicYearEndDate,
    allowBackDate,
    preventFutureOpenDate: false,
    initialTimeMoment,
    opensOn,
  });
  const dueDateRange = getDueDateRange({
    academicYearEndDate,
    initialTimeMoment,
    opensOn,
    dueOn,
  });
  const closeDateRange = getCloseDateRange({
    academicYearEndDate,
    initialTimeMoment,
    opensOn,
    dueOn,
    closesOn,
  });

  useAssignmentDateValidation({
    dueDate,
    closeSubmissionDate,
    publishedAt,
    setFooterDisabled,
    errorMsgLocalesRef,
    isNotUserCourse,
  });
  const getDateAndtimeContentByUserType = () => {
    // Assignments
    if (isAssignment) {
      const dateChangeFunctionsMapping = {
        openDate: onUpdatePublishedDate,
        dueDate: onUpdateDueDate,
        closeDate: onUpdateCloseSubmissionDate,
      };

      const timeChangeFunctionsMapping = {
        openDate: onUpdatePublishedTime,
        dueDate: onUpdateDueTime,
        closeDate: onUpdateCloseSubmissionTime,
      };
      const onDateChange = (field, newDateTime, type) => {
        const updateFunction = dateChangeFunctionsMapping[field];
        if (updateFunction && type === "date") {
          updateFunction(newDateTime, type);
        }
        const timeUpdateFunction = timeChangeFunctionsMapping[field];
        if (timeUpdateFunction && type === "time") {
          timeUpdateFunction(newDateTime, type);
        }
      };

      const isNewQuickViewEnabled = isNewQuickViewEditFFEnabled();

      //1. can edit (admin & staff that are on this class)
      if (isStaff && isUserCourse) {
        return isNewQuickViewEnabled ? (
          <div className={classes.assignmentDateTimeFieldsContainer}>
            <AssignmentDateTimeFields
              t={t}
              dueDate={dueDate}
              openDate={opensOn}
              closeDate={closeSubmissionDate}
              onDateChange={onDateChange}
              isCurrentAcademicYear={isCurrentAcademicYear}
              isGradingPeriodUnlocked={isGradingPeriodUnlocked}
              isEditMode={
                !(
                  !isCurrentAcademicYear ||
                  !isGradingPeriodUnlocked ||
                  !canManageAssignment
                )
              }
            />
          </div>
        ) : (
          <div>
            {!isWorksheetWithFixedStartTime ? (
              <div className={classes.dueTimeNonEditableText}>
                <div
                  className={classNames("text-label", classes.dueOnDateLabel)}
                >
                  {isCalendarAssignmentAssignFlowRevampEnabled
                    ? t("calendar:due_on")
                    : t("common:due_date")}
                </div>
                {isVaildDueDate ? (
                  <div className={editableDueDateTimeStyles}>
                    <div className={classes.datePickerContainer}>
                      <ConditionalWrapper
                        condition={!isGradingPeriodUnlocked}
                        wrapper={
                          <Tooltip tooltip={lockedTermDisabledTooltip} />
                        }
                        type={"wrapperAsElement"}
                      >
                        <DatePicker
                          onChange={onUpdateDueDate}
                          picker="date"
                          format={DATE_TIME_CONSTANTS.DD_MMM_ddd}
                          value={dueDateMomentObject}
                          variant="inline"
                          inputReadOnly={true}
                          minDate={dueDateRange?.minDate}
                          maxDate={dueDateRange?.maxDate}
                          testId={testId}
                          dsVersion={"1.0"}
                          disabled={
                            !isCurrentAcademicYear ||
                            !isGradingPeriodUnlocked ||
                            !canManageAssignment
                          }
                        />
                      </ConditionalWrapper>
                    </div>
                    <TimeSelectorByPerm
                      value={dueDateMomentObject}
                      updateInputField={onUpdateDueTime}
                      {...getTimeRangeProps({ dueDateRange })}
                      disableConfirmButton={setIsFromTimeInvalid}
                      scheduleDate={dueDateMomentObject}
                      isCurrentAcademicYear={isCurrentAcademicYear}
                      isGradingPeriodUnlocked={isGradingPeriodUnlocked}
                      lockedTermDisabledTooltip={lockedTermDisabledTooltip}
                      testId={testId}
                      canManageAssignment={canManageAssignment}
                      showError={
                        isCalendarAssignmentAssignFlowRevampEnabled &&
                        isDueDatePassed
                      }
                    />
                  </div>
                ) : (
                  <div className={classNames("text-body", classes.published)}>
                    {t("calendar:no_due_date")}
                  </div>
                )}
              </div>
            ) : null}

            <div className={classes.scheduleDateRow}>
              {isFutureDateTime(scheduleDate) && !isStaff ? null : (
                <div className={classes.dueTimeNonEditableText}>
                  <div
                    className={classNames("text-label", classes.dueOnDateLabel)}
                  >
                    {isCalendarAssignmentAssignFlowRevampEnabled
                      ? getSubmissionOpenLabel({
                          t,
                          eventDateTime: publishedAtMomentObject,
                        })
                      : getDateLabel({
                          showCalendarAssignmentByDueDate,
                          t,
                          eventDateTime: publishedAtMomentObject,
                          eventEndDateTime: scheduleDate,
                          isSchoolCalendarEvent:
                            itemType ===
                            MY_CALENDAR_CONSTANTS.SCHOOL_CALENDAR_EVENT,
                          isDeadlineEvent:
                            itemType ===
                            MY_CALENDAR_CONSTANTS.PROJECT_DEADLINE_EVENT,
                        })}
                  </div>
                  <div className={classes.dateTimeContainer}>
                    <div className={classes.datePickerContainer}>
                      <ConditionalWrapper
                        condition={!isGradingPeriodUnlocked}
                        wrapper={
                          <Tooltip tooltip={lockedTermDisabledTooltip} />
                        }
                        type={"wrapperAsElement"}
                      >
                        <DatePicker
                          onChange={onUpdatePublishedDate}
                          picker="date"
                          format={DATE_TIME_CONSTANTS.DD_MMM_ddd}
                          value={publishedAtMomentObject}
                          variant="inline"
                          inputReadOnly={true}
                          minDate={openDateRange?.minDate}
                          maxDate={openDateRange?.maxDate}
                          testId={testId}
                          dsVersion={"1.0"}
                          disabled={
                            !isCurrentAcademicYear ||
                            !isGradingPeriodUnlocked ||
                            !canManageAssignment
                          }
                        />
                      </ConditionalWrapper>
                    </div>
                    <TimeSelectorByPerm
                      value={publishedAtMomentObject}
                      updateInputField={onUpdatePublishedTime}
                      {...getTimeRangeProps({
                        openDateRange,
                      })}
                      disableConfirmButton={setIsFromTimeInvalid}
                      scheduleDate={publishedAt}
                      isCurrentAcademicYear={isCurrentAcademicYear}
                      isGradingPeriodUnlocked={isGradingPeriodUnlocked}
                      lockedTermDisabledTooltip={lockedTermDisabledTooltip}
                      testId={testId}
                      canManageAssignment={canManageAssignment}
                    />
                    {!showCalendarAssignmentByDueDate &&
                    allowAssignmentScheduleEndTimeChange ? (
                      <>
                        <div className={classes.timeBetweenText}>{"-"}</div>
                        <TimeSelectorByPerm
                          value={calendarAssignmentEndAtMomentObject}
                          updateInputField={onUpdateCalendarAssignmentEndAt}
                          startTime={formatDate({
                            date: publishedAtMomentObject,
                            format: DATE_TIME_CONSTANTS.HH_mm,
                          })}
                          endTime={START_PICKER_END_TIME}
                          disableConfirmButton={setIsToTimeInvalid}
                          scheduleDate={calendarAssignmentEndAt}
                          isCurrentAcademicYear={isCurrentAcademicYear}
                          isGradingPeriodUnlocked={isGradingPeriodUnlocked}
                          lockedTermDisabledTooltip={lockedTermDisabledTooltip}
                          testId={testId}
                          canManageAssignment={canManageAssignment}
                        />
                      </>
                    ) : null}
                  </div>
                </div>
              )}

              {isCalendarAssignmentAssignFlowRevampEnabled &&
              isVaildCloseSubmissionDate &&
              !isWorksheetWithFixedStartTime ? (
                <div className={classes.dueTimeNonEditableText}>
                  <div
                    className={classNames("text-label", classes.dueOnDateLabel)}
                  >
                    {getSubmissionCloseLabel({
                      t,
                      eventDateTime: closeSubmissionDateMomentObject,
                    })}
                  </div>
                  <div className={editableCloseSubmissionDateStyles}>
                    <div className={classes.datePickerContainer}>
                      <ConditionalWrapper
                        condition={!isGradingPeriodUnlocked}
                        wrapper={
                          <Tooltip tooltip={lockedTermDisabledTooltip} />
                        }
                        type={"wrapperAsElement"}
                      >
                        <DatePicker
                          onChange={onUpdateCloseSubmissionDate}
                          picker="date"
                          format={DATE_TIME_CONSTANTS.DD_MMM_ddd}
                          value={closeSubmissionDateMomentObject}
                          variant="inline"
                          inputReadOnly={true}
                          minDate={closeDateRange?.minDate}
                          maxDate={closeDateRange?.maxDate}
                          testId={testId}
                          dsVersion={"1.0"}
                          disabled={
                            !isCurrentAcademicYear ||
                            !isGradingPeriodUnlocked ||
                            !canManageAssignment
                          }
                        />
                      </ConditionalWrapper>
                    </div>
                    <TimeSelectorByPerm
                      value={closeSubmissionDateMomentObject}
                      updateInputField={onUpdateCloseSubmissionTime}
                      {...getTimeRangeProps({ closeDateRange })}
                      disableConfirmButton={setIsFromTimeInvalid}
                      scheduleDate={closeSubmissionDate}
                      isCurrentAcademicYear={isCurrentAcademicYear}
                      isGradingPeriodUnlocked={isGradingPeriodUnlocked}
                      lockedTermDisabledTooltip={lockedTermDisabledTooltip}
                      testId={testId}
                      canManageAssignment={canManageAssignment}
                      showError={
                        isCalendarAssignmentAssignFlowRevampEnabled &&
                        isCloseSubmissionDatePassed
                      }
                    />
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        );
      }
      //2. cannot edit (student/parent & staff that are NOT in this class)
      else {
        return isNewQuickViewEnabled ? (
          <div className={classes.assignmentDateTimeFieldsContainer}>
            <AssignmentDateTimeFields
              t={t}
              dueDate={dueDate}
              openDate={opensOn}
              closeDate={closeSubmissionDate}
              onDateChange={onDateChange}
              isCurrentAcademicYear={isCurrentAcademicYear}
              isGradingPeriodUnlocked={isGradingPeriodUnlocked}
              isEditMode={false}
            />
          </div>
        ) : (
          <div>
            {!isWorksheetWithFixedStartTime ? (
              <div className={classes.dueTimeNonEditableText}>
                <div
                  className={classNames("text-label", classes.dueOnDateLabel)}
                >
                  {t("common:due_on")}
                </div>
                <div className={nonEditableDueDateTimeStyles}>
                  {isVaildDueDate
                    ? formatDate({
                        date: deadline,
                        format: DATE_TIME_CONSTANTS.DD_MMM_YYYY_hh_mm_A,
                      })
                    : t("calendar:no_due_date")}
                </div>
              </div>
            ) : null}
            {isFutureDateTime(scheduleDate) &&
            !isStaff &&
            !isCalendarAssignmentAssignFlowRevampEnabled ? null : (
              <div className={classes.scheduleDateRow}>
                <div className={classes.dueTimeNonEditableText}>
                  <div
                    className={classNames("text-label", classes.dueOnDateLabel)}
                  >
                    {isCalendarAssignmentAssignFlowRevampEnabled
                      ? getSubmissionOpenLabel({
                          t,
                          eventDateTime: publishedAtMomentObject,
                        })
                      : getDateLabel({
                          showCalendarAssignmentByDueDate,
                          t,
                          eventDateTime: moment(
                            submissionDeadlineTime,
                            DATE_TIME_CONSTANTS.ddd_MMM_DD_hh_mm_A
                          ),
                          eventEndDateTime: scheduleDate,
                          isSchoolCalendarEvent:
                            itemType ===
                            MY_CALENDAR_CONSTANTS.SCHOOL_CALENDAR_EVENT,
                          isDeadlineEvent:
                            itemType ===
                            MY_CALENDAR_CONSTANTS.PROJECT_DEADLINE_EVENT,
                        })}
                  </div>
                  <div className={classNames("text-body", classes.published)}>
                    {moment(
                      submissionDeadlineTime,
                      DATE_TIME_CONSTANTS.ddd_MMM_DD_hh_mm_A
                    ).format(DATE_TIME_CONSTANTS.DD_MMM_YYYY_hh_mm_A)}
                  </div>
                </div>
                {isCalendarAssignmentAssignFlowRevampEnabled &&
                isVaildCloseSubmissionDate &&
                !isStaff &&
                getIsSubmissionOpen({
                  calendarWiseAssignedUsers,
                  isParent,
                  childId,
                  userId,
                }) &&
                !isWorksheetWithFixedStartTime ? (
                  <div className={classes.dueTimeNonEditableText}>
                    <div
                      className={classNames(
                        "text-label",
                        classes.dueOnDateLabel
                      )}
                    >
                      {getSubmissionCloseLabel({
                        t,
                        eventDateTime: closeSubmissionDateMomentObject,
                      })}
                    </div>
                    <div className={nonEditableCloseSubmissionDateStyles}>
                      {formatDate({
                        date: closeSubmissionDateMomentObject,
                        format: DATE_TIME_CONSTANTS.DD_MMM_YYYY_hh_mm_A,
                      })}
                    </div>
                  </div>
                ) : null}
              </div>
            )}
          </div>
        );
      }
    }
    // Meeting
    else {
      //1. can edit only the staff members that are in any one meeting's class
      if (isStaff && isUserCourse) {
        return (
          <div className={classes.dueTimeNonEditableText}>
            <div className={classNames("text-label", classes.dueOnDateLabel)}>
              {getVideoMeetingDateLabel({
                showCalendarAssignmentByDueDate,
                t,
                isVideoMeeting,
                eventDateTime: scheduleDate,
                isSchoolCalendarEvent:
                  itemType === MY_CALENDAR_CONSTANTS.SCHOOL_CALENDAR_EVENT,
                isDeadlineEvent:
                  itemType === MY_CALENDAR_CONSTANTS.PROJECT_DEADLINE_EVENT,
              })}
            </div>
            <div className={classes.dateTimeContainer}>
              <div className={classes.datePickerContainer}>
                <DatePicker
                  onChange={onUpdateDateInput}
                  picker="date"
                  format={DATE_TIME_CONSTANTS.DD_MMM_ddd}
                  value={getDateMomentObjectCustom(
                    scheduleDate,
                    DATE_TIME_CONSTANTS.YYYY_MM_DD
                  )}
                  variant="inline"
                  inputReadOnly={true}
                  minDate={getDateMomentObject()}
                  maxDate={maxStartDate}
                  testId={testId}
                  disabled={!isCurrentAcademicYear}
                  dsVersion={"1.0"}
                />
              </div>
              <TimeSelectorByPerm
                value={scheduleStartTime}
                updateInputField={onStartTimeChange}
                startTime={START_PICKER_START_TIME}
                endTime={START_PICKER_END_TIME}
                disableConfirmButton={setIsFromTimeInvalid}
                scheduleDate={scheduleDate}
                isCurrentAcademicYear={isCurrentAcademicYear}
                testId={testId}
              />

              <div className={classes.timeBetweenText}>{"-"}</div>

              <TimeSelectorByPerm
                value={scheduleEndTime}
                updateInputField={onEndTimeChange}
                startTime={scheduleStartTime}
                endTime={START_PICKER_END_TIME}
                disableConfirmButton={setIsToTimeInvalid}
                scheduleDate={scheduleDate}
                isCurrentAcademicYear={isCurrentAcademicYear}
                testId={testId}
              />
            </div>
          </div>
        );
      }
      //2. cannot edit (student/parent & staff that are NOT in this class)
      else {
        return (
          <div className={classes.dueTimeNonEditableText}>
            <div className={classNames("text-label", classes.dueOnDateLabel)}>
              {getVideoMeetingDateLabel({
                showCalendarAssignmentByDueDate,
                t,
                isVideoMeeting,
                eventDateTime: scheduleDate,
                isSchoolCalendarEvent:
                  itemType === MY_CALENDAR_CONSTANTS.SCHOOL_CALENDAR_EVENT,
                isDeadlineEvent:
                  itemType === MY_CALENDAR_CONSTANTS.PROJECT_DEADLINE_EVENT,
              })}
            </div>
            <div className={classNames("text-body", classes.timeDateText)}>
              {formatDate({
                date: getDateMomentObjectCustom(
                  scheduleStartTime,
                  DATE_TIME_CONSTANTS.hh_mm_a
                ),
                format: DATE_TIME_CONSTANTS.hh_mm_A,
              })}
              <div className={classes.timeBetweenText}>{"-"}</div>
              {formatDate({
                date: getDateMomentObjectCustom(
                  scheduleEndTime,
                  DATE_TIME_CONSTANTS.hh_mm_a
                ),
                format: DATE_TIME_CONSTANTS.hh_mm_A,
              })}
              <div className={classes.dateTextContainer}>
                {formatDate({
                  date: getDateMomentObjectCustom(
                    scheduleDate,
                    DATE_TIME_CONSTANTS.YYYY_MM_DD
                  ),
                  format: DATE_TIME_CONSTANTS.DD_MMM_ddd,
                })}
              </div>
            </div>
          </div>
        );
      }
    }
  };

  const StateTagAndDescriptionForEvent = () => {
    let tagValue = "",
      tagColor = "",
      description = "";

    if (isDraft) {
      tagValue = t("common:draft");
      tagColor = "yellow";
    } else if (!isCalendarAssignmentAssignFlowRevampEnabled) {
      return null;
    } else if (visibility === "PRIVATE") {
      tagValue = t("calendar:not_yet_shared_with_students");
      tagColor = "pink";
      description = t("calendar:assignment_is_currently_only_visible");
    } else if (visibility === "VISIBLE") {
      description = t("calendar:this_assignment_is_visible_to_the_students");
    } else if (visibility === "TO_BE_VISIBLE") {
      if (state === "SCHEDULED") {
        description = t(
          "calendar:this_assignment_will_be_shared_to_students_on",
          {
            date: formatDate({
              date: publishedAtMomentObject,
              format: DATE_TIME_CONSTANTS.DD_MMM,
            }),
            time: formatDate({
              date: publishedAtMomentObject,
              format: DATE_TIME_CONSTANTS.hh_mm_A,
            }),
          }
        );
      } else if (state === "PUBLISHED") {
        description = t("calendar:this_assignment_is_visible_to_the_students");
      }
    } else {
      description = "";
    }

    const descriptionClasses = classNames(
      "text-body",
      classes.descriptionContainter
    );

    return (
      <div className={classes.tagDescriptionContainter}>
        {_.size(tagValue) ? (
          <div className={classes.tagContainter}>
            <Tag size="medium" color={tagColor} dsVersion="2.0">
              {tagValue}
            </Tag>
          </div>
        ) : null}
        {_.size(description) ? (
          <div className={descriptionClasses}>{description}</div>
        ) : null}
      </div>
    );
  };

  const SubmissionStateForEvent = () => {
    if (!isCalendarAssignmentAssignFlowRevampEnabled) return null;

    // We dont need to render status of student in meeting event type
    if (isVideoMeeting) return null;

    const currentStudentStatus = _.find(
      _.get(_.first(calendarWiseAssignedUsers), "assignedUsers", []),
      ({ id }) => id === (isParent ? childId : userId)
    );

    const { statusV2, submittedAt } = currentStudentStatus;

    const submissionState = getSubmissionStatus({
      t,
      submissionStatus: statusV2,
      endTime: dueDate,
      submittedAt,
    });

    const {
      backgroundColor,
      icon,
      parentSubmissionStatusLabel,
      studentSubmissionStatusLabel,
      showSubmissionStatus = true,
    } = submissionState;

    return showSubmissionStatus ? (
      <div
        className={classes.submissionStateContainer}
        style={{ backgroundColor: backgroundColor }}
      >
        <div className={classes.submissionStateIcon}>{icon}</div>
        <div className={classes.submissionStateText}>
          <div className={"text-body"}>
            {/* We're using parent status locales if staff is viewing student calendar */}
            {isParent || isStudentProfileCalendarView
              ? parentSubmissionStatusLabel
              : studentSubmissionStatusLabel}
          </div>
          {statusV2 === "TURNED_IN_LATE" ? (
            <div className={"text-label-s"}>
              {t("classRoom:late_submission")}
            </div>
          ) : null}
        </div>
      </div>
    ) : null;
  };

  const isVideoMeetingRevampEnabled = isNewVideoMeetingUIFeatureEnabled();
  const isVideoMeetingAndRevampEnabled =
    isVideoMeeting && isVideoMeetingRevampEnabled;

  if (isVideoMeetingAndRevampEnabled) {
    return (
      <EventBodyMeeting
        testId={`${testId}-eventBodyMeeting`}
        title={title}
        meetingLink={meetingLink}
        meetingType={meetingType}
        meetingCode={meetingCode}
        meetingPasscode={meetingPasscode}
        description={description}
        associatedEntities={associatedEntities}
        sharedWith={sharedWith}
        isStaff={isStaff}
        users={users}
        attachments={attachments}
        isAssociatedEntitiesDetailsLoading={isAssociatedEntitiesDetailsLoading}
        // Common props
        isCurrentAcademicYear={isCurrentAcademicYear}
        academicYearEndDate={academicYearEndDate}
        isParent={isParent}
        userId={userId}
        // Date and Time props
        scheduleDate={scheduleDate}
        maxStartDate={maxStartDate}
        onUpdateDateInput={onUpdateDateInput}
        // Time props
        scheduleStartTime={scheduleStartTime}
        scheduleEndTime={scheduleEndTime}
        onStartTimeChange={onStartTimeChange}
        onEndTimeChange={onEndTimeChange}
        repeatEventSettings={repeatEventSettings}
        recurrenceStart={recurrenceStart}
        startTime={startTime}
        metadata={metadata}
        canUserEditThisMeeting={canUserEditThisMeeting}
        isStudentProfileCalendarView={isStudentProfileCalendarView}
        //loading states
        isMeetingDetailsLoading={isMeetingDetailsLoading}
      />
    );
  }

  const assignedStudents = _.map(
    _.first(calendarWiseAssignedUsers)?.assignedUsers,
    ({ id, name, profileImage, alertTypes }) => ({
      id,
      firstName: name,
      profileImage,
      alertTypes,
      __typename: "Student",
    })
  );

  const studentCount = _.size(assignedStudents);
  const summaryText = _.first(calendarWiseAssignedUsers)?.title || "";

  return (
    <div className={classes.body}>
      <div className={classes.headerTitle}>
        <div className={classes.eventTitleContainer}>
          <Tooltip tooltip={title} dsVersion="2.0">
            <h5 className={classNames(classes.eventTitle)} dir="auto">
              {title}
            </h5>
          </Tooltip>
          <NewQuickViewFeatureFlagWrapper>
            {isStaff ? (
              <StateTagAndDescriptionForEvent />
            ) : (
              <SubmissionStateForEvent />
            )}
          </NewQuickViewFeatureFlagWrapper>
          <div className={classes.publishedTime}>
            {getDateAndtimeContentByUserType()}
          </div>
        </div>
      </div>
      {description ? (
        <>
          <div className={classNames("text-label", classes.label)}>
            {t("common:description")}
          </div>
          <div
            className={classNames("text-body", classes.descriptionText)}
            dangerouslySetInnerHTML={{
              __html: getStyleStrippedText({
                text: getStringAutoLinks(replaceSlashNWithBRTag(description)),
                type: "SANITIZE_DANGER_HTML_PARAGRAPH",
              }),
            }}
          ></div>
        </>
      ) : null}
      {meetingLink && !isNotUserCourse && !isParent ? (
        <div className={classes.meeting}>
          <div className={classNames("text-label", classes.meetingText)}>
            {`${t("calendar:meeting_link")}`}
            <IconButton
              icon={<CopyOutlined variant={"subtle"} />}
              onClick={onCopyURL}
              variant={"neutral"}
              type={"plain"}
              size={"medium"}
              isCompact={true}
              testId={`${testId}-copylink`}
              dsVersion={"2.0"}
            />
          </div>
          <div className={classes.meetingLinkBackground}>
            <div
              className={classNames("text-body", classes.meetingLink)}
              dangerouslySetInnerHTML={{
                __html: getStyleStrippedText({
                  text: getStringAutoLinks(meetingLink),
                  type: "SANITIZE_DANGER_HTML_PARAGRAPH",
                }),
              }}
            ></div>
          </div>
        </div>
      ) : null}

      {isMeetingDetailsLoading ? (
        <ParticipantLoadingSkeleton />
      ) : isNewParticipantSelectorEnabled() ? (
        <div className={classes.courseDetailsV2}>
          <ParticipantsSummary
            label={t("common:class")}
            totalCount={studentCount}
            summaryText={summaryText}
            eventTitle={title}
            associatedEntities={assignedStudents}
            sharedWith={["STUDENT"]}
            staffCount={0}
            studentCount={studentCount}
            familyCount={0}
            participantModalLabel={t("common:student_plural")}
            eventType={itemType}
          />
        </div>
      ) : (
        <CalendarWiseAssignedUsers
          t={t}
          calendarWiseAssignedUsers={calendarWiseAssignedUsers}
          isParent={isParent}
          testId={testId}
          userId={userId}
          childId={childId}
          calendars={calendars}
          isStaff={isStaff}
          submissionsSummaryCounts={submissionsSummaryCounts}
          isVideoMeeting={isVideoMeeting}
          isDueDatePassed={isDueDatePassed}
          isCalendarAssignmentAssignFlowRevampEnabled={
            isCalendarAssignmentAssignFlowRevampEnabled
          }
          isStudentSubmissionEnabled={isStudentSubmissionEnabled}
        />
      )}
    </div>
  );
};

export default memo(EventBody);
